import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const API_URL = process.env.REACT_APP_API_BASE_URL || (process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api');

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token') || null);
  const [loading, setLoading] = useState(true);

  // Axios Interceptor for token refresh
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // If error is 401 and not already retried
        if (error.response?.status === 401 && !originalRequest._retry) {
          // Don't try to refresh if the error comes from the login or refresh endpoints themselves
          if (originalRequest.url.includes('/auth/login') || originalRequest.url.includes('/auth/refresh')) {
            return Promise.reject(error);
          }

          originalRequest._retry = true;
          const refreshToken = localStorage.getItem('refreshToken');

          if (refreshToken) {
            try {
              console.log('🔄 [AuthContext] Token expired, attempting refresh...');
              const response = await axios.post(`${API_URL}/auth/refresh`, { refreshToken });
              const { token: newToken, refreshToken: newRefreshToken, user: userData } = response.data;

              handleAuthSuccess(newToken, newRefreshToken, userData);

              // Update the original request header and retry
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              return axios(originalRequest);
            } catch (refreshError) {
              console.error('❌ [AuthContext] Refresh token failed:', refreshError);
              logout();
              return Promise.reject(refreshError);
            }
          } else {
            logout();
          }
        }
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  useEffect(() => {
    // Validar token al cargar la aplicación
    const validateToken = async () => {
      const storedToken = localStorage.getItem('token');

      if (storedToken) {
        try {
          // Verificar que el token es válido intentando obtener datos del usuario
          axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
          const response = await axios.get(`${API_URL}/users/me`);
          setUser(response.data);
          setToken(storedToken);
        } catch (error) {
          console.log('⚠️ Token inválido o expirado al inicio');
          // No llamamos a logout inmediatamente para dar oportunidad al refresh interceptor
          // si el error fue un 401. Pero si falla todo, el interceptor hará el logout.
        }
      }
      setLoading(false);
    };

    validateToken();
  }, []);

  const login = async (email, password, twoFactorCode = '') => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { email, password, twoFactorCode });
      const { token: newToken, refreshToken, user: userData } = response.data;

      handleAuthSuccess(newToken, refreshToken, userData);
      return { success: true, user: userData };
    } catch (error) {
      if (error.response?.data?.twoFactorRequired) {
        return { success: true, twoFactorRequired: true };
      }
      return { success: false, message: error.response?.data?.msg || 'Login failed' };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API_URL}/auth/register`, userData);
      const { token: newToken, refreshToken, user: newUserData } = response.data;

      handleAuthSuccess(newToken, refreshToken, newUserData);
      return { success: true };
    } catch (error) {
      return { success: false, message: error.response?.data?.msg || 'Registration failed' };
    }
  };

  const handleAuthSuccess = (newToken, refreshToken, userData) => {
    setToken(newToken);
    setUser(userData);
    localStorage.setItem('token', newToken);
    localStorage.setItem('refreshToken', refreshToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/me`);
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [AuthContext] Error refreshing user:', error);
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('mock_session');
    localStorage.removeItem('mock_user');
    delete axios.defaults.headers.common['Authorization'];
    // Forzar recarga opcional para limpiar estado de la app si es necesario
    // window.location.href = '/'; 
  };

  const value = {
    user,
    token,
    login,
    register,
    logout,
    refreshUser,
    loading,
    isAdmin: user?.roles?.some(r => typeof r === 'string' ? (r === 'ADMIN' || r === 'SUPER_ADMIN') : (r.name === 'ADMIN' || r.name === 'SUPER_ADMIN')),
    isInvestigator: user?.roles?.some(r => typeof r === 'string' ? r === 'INVESTIGATOR' : r.name === 'INVESTIGATOR'),
    isProfessional: user?.roles?.some(r => typeof r === 'string' ? r === 'HEALTH_PROFESSIONAL' : r.name === 'HEALTH_PROFESSIONAL'),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};